import type { QueryClient } from "@tanstack/react-query";

/**
 * Refetch every cached query whose data depends on whether an entity has an
 * active accounting connection (OCT-18620).
 *
 * The bill, bill-scan and invoice GL pickers read `/api/chart-of-accounts/synced`,
 * which returns the connection's synced accounts, or only OCTA-owned accounts
 * when the entity has no connection (OCT-18523). Cached queries here never
 * refetch on their own, so without this a user who disconnects Zoho Books and
 * opens Add a Bill in the same session still sees the old Zoho accounts.
 *
 * Each key is listed on its own: TanStack matches key prefixes element by
 * element, so `["/api/chart-of-accounts"]` does not cover
 * `["/api/chart-of-accounts/synced", entityId]`. The keys are bare paths so
 * they match every entity segment, including the `"all"` sentinel the forms
 * use, which a `[path, activeEntityId]` key would miss.
 */
export const ACCOUNTING_CONNECTION_SCOPED_QUERY_KEYS = [
  ["/api/chart-of-accounts/synced"],
  ["/api/chart-of-accounts/integration-status"],
  ["/api/chart-of-accounts/deposit-accounts"],
] as const;

/**
 * True for a request that can connect, disconnect, reconnect or sync an
 * accounting connection: any write under `/api/integrations/`.
 *
 * `apiRequest` checks this after every successful response, so a new
 * connection-change path is covered without calling anything by hand. The
 * match is broad on purpose: a false positive costs one refetch of a mounted
 * picker (inactive queries are only marked stale), a false negative is the
 * OCT-18620 bug.
 */
export function isAccountingConnectionChangeRequest(method: string, url: string): boolean {
  const verb = method.toUpperCase();
  if (verb === "GET" || verb === "HEAD" || verb === "OPTIONS") return false;
  return new URL(url, "http://octa.local").pathname.startsWith("/api/integrations/");
}

/**
 * Never rejects, so callers can fire and forget it. A failed refetch already
 * lands in the query's own error state, which is what the pickers render.
 *
 * Only call it by hand for a connection change OCTA makes no request for,
 * such as an Apideck Vault SDK callback. Every OCTA request is covered by
 * `apiRequest` through {@link isAccountingConnectionChangeRequest}.
 */
export async function invalidateAccountingConnectionScopedQueries(queryClient: QueryClient): Promise<void> {
  try {
    await Promise.all(
      ACCOUNTING_CONNECTION_SCOPED_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  } catch (err) {
    console.warn("[accounting-connection-change] failed to refresh GL picker queries", err);
  }
}
